import Dialog from '@pelckmans/elementis/components/dialog';
import Pill from '@pelckmans/elementis/components/pill';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import Results from '@pelckmans/elementis/components/results';
import Time from '@pelckmans/elementis/icons/time';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Medialink } from '../../../../../../api/medialink';
import { Module } from '../../../../../../api/module';
import { MedialinkDialogHeaderTagsWithoutPillGroup } from '../../../../../../pages/material/components/MedialinkDialogHeaderTags';
import { formatDuration, getResultPercentages, getShowScore } from '../../../../../../utils/results';
import { TOKEN_ACCESSOR } from '../../../../../../utils/tokenAccessor';
import { ANALYTICS_EVENT_TYPES, AnalyticsData } from '../../../../hooks/constants/Analytics';
import useAnalytics from '../../../../hooks/useAnalytics';
import { calculateScore } from '../../../../util/calculateScore';
import { reviewExerciseSession } from '../../api/exercises-api';
import { ExerciseIframe } from '../exercise-iframe';
import { ExerciseLoading } from '../exercise-loading';
import ServiceNotAvailable from '../service-not-available';

interface Session {
  duration: number;
  score: {
    maximum: number;
    achievable: number;
    achieved: number;
  };
  lti?: {
    gradingProgress: 'FullyGraded' | 'Pending' | 'PendingManual' | 'Failed' | 'NotReady';
  };
  status?: 'not-started' | 'busy' | 'not-finished' | 'finished' | 'pending-grading';
}

interface Props {
  id: string;
  analyticsData?: AnalyticsData;
  medialink: Medialink;
  module: Module;
  onClose: () => void;
  studioGroup: string;
  title: string;
  session?: Session;
  sessionId: string;
}

function ExerciseAnswersModal({
  id,
  analyticsData,
  medialink,
  module,
  onClose,
  studioGroup,
  title,
  session,
  sessionId,
}: Props): JSX.Element | null {
  const [url, setUrl] = useState<string>();
  const [serviceAvailable, setServiceAvailable] = useState<boolean>(true);
  const eventCaptured = useRef<boolean>(false);
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const { duration } = session || {};
  const { achieved, maximum } = session?.score || {};
  const showScore = getShowScore(medialink);

  useEffect(() => {
    const run = async (): Promise<void> => {
      const token = await TOKEN_ACCESSOR.tokenGetter();

      try {
        const { reviewUrl } = await reviewExerciseSession(
          token!,
          medialink.moduleId,
          medialink.id,
          sessionId,
          studioGroup,
        );

        setUrl(reviewUrl);
      } catch (err) {
        setServiceAvailable(false);
      }
    };

    run();
  }, [medialink, sessionId, studioGroup]);

  useEffect(() => {
    const captureEvent = async (): Promise<void> => {
      const token = await TOKEN_ACCESSOR.tokenGetter();
      if (analyticsData && token && !eventCaptured.current) {
        eventCaptured.current = true;
        analytics.capture(
          {
            eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
            objectId: medialink.id,
            analyticsData,
          },
          token,
        );
      }
    };

    captureEvent();
  }, [analytics, analyticsData, medialink]);

  const handleClose = () => {
    onClose();
  };

  const size: ComponentProps<typeof Dialog>['size'] = 'full';

  return (
    <Dialog
      id={id}
      modal
      onClose={handleClose}
      aria-label={t('EXERCISE_MANAGER.EXERCISE_ANSWERS_MODAL.A11Y_LABEL')}
      size={size}
    >
      <Dialog.Header
        title={title}
        onClose={handleClose}
        aria-label={t('EXERCISE_MANAGER.CLOSE_DIALOG')}
        showCloseLabelAsText={size === 'full'}
        titleTooltip={medialink.name}
        meta={
          <PillGroup aria-label={undefined} role={undefined}>
            <MedialinkDialogHeaderTagsWithoutPillGroup material={medialink} module={module} />
            {session && (
              <>
                {duration && (
                  <Pill key="duration" icon={<Time />}>
                    {formatDuration(duration)}
                  </Pill>
                )}
                {!medialink.unachievable && achieved !== undefined && maximum !== undefined && (
                  <Pill key="percentage">{calculateScore({ achieved, maximum })}%</Pill>
                )}
                {showScore && !medialink.unachievable && <Pill key="raw-score">{`${achieved} / ${maximum}`}</Pill>}
              </>
            )}
            {session && (
              <Pill key="results-pill">
                <Results
                  {...getResultPercentages(session.score, session.lti?.gradingProgress === 'PendingManual')}
                  quantifiable={!medialink.unachievable}
                  status={session.status}
                  showTooltip
                  responsive={false}
                  size="sm"
                />
              </Pill>
            )}
          </PillGroup>
        }
      />
      <Dialog.Body>
        {!serviceAvailable ? (
          <ServiceNotAvailable />
        ) : (
          <>
            {url ? (
              <ExerciseIframe exerciseUrl={url} medialink={medialink} onExerciseFinished={handleClose} />
            ) : (
              <ExerciseLoading
                loadingText={{
                  title: t('EXERCISE_MANAGER.EXERCISE_LOADING.TITLE'),
                  p1: t('EXERCISE_MANAGER.EXERCISE_LOADING.P1'),
                  p2: t('EXERCISE_MANAGER.EXERCISE_LOADING.P2'),
                }}
              />
            )}
          </>
        )}
      </Dialog.Body>
    </Dialog>
  );
}

export default ExerciseAnswersModal;
